import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { StickyBackBar } from '@/components/layout/StickyBackBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Spinner } from '@/components/ui/Spinner';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { useOwnEntities, usePurgeOwnAccount } from '@/api/hooks/useAccountPurge';
import {
    ExclamationTriangleIcon,
    ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const CONFIRMATION_PHRASE = 'DELETE MY DATA';

export function PurgeDataPage() {
    usePageTitle('Purge Data');
    const navigate = useNavigate();
    const { refreshProfile } = useAuth();

    const [inputPhrase, setInputPhrase] = useState('');
    const [hasAcknowledgedBackup, setHasAcknowledgedBackup] = useState(false);
    const { data, isLoading } = useOwnEntities();
    const purgeAccount = usePurgeOwnAccount();

    const totalEntities =
        (data?.collectionsCount ?? 0) +
        (data?.tagsCount ?? 0) +
        (data?.ledgersCount ?? 0);

    const totalValues =
        data?.ledgers?.reduce((sum, l) => sum + l.valuesCount, 0) ?? 0;
    const totalReturns =
        data?.ledgers?.reduce((sum, l) => sum + l.returnsCount, 0) ?? 0;

    const isPhraseCorrect = inputPhrase === CONFIRMATION_PHRASE;

    const handlePurge = async () => {
        if (!isPhraseCorrect || !hasAcknowledgedBackup) {
            return;
        }

        try {
            const result = await purgeAccount.mutateAsync(inputPhrase);
            // Refresh auth profile to update hasAssetCollections/hasDebtCollections flags
            await refreshProfile();
            toast.success(result.message);
            navigate('/data');
        } catch {
            toast.error('Failed to purge account data. Please try again.');
        }
    };

    return (
        <Layout>
            <StickyBackBar destinationLabel="Data" backPath="/data" />

            <div className="space-y-6 pt-4">
                <PageHeader title="Purge Data" />
                {/* Warning and backup inline on larger screens */}
                <div className="grid gap-4 md:grid-cols-2">
                    {/* Warning */}
                    <div className="flex items-start gap-3 rounded-lg bg-red-50 p-4 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400 mt-0.5" />
                        <div>
                            <p className="font-medium text-red-800 dark:text-red-300">
                                This action cannot be undone
                            </p>
                            <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                                All your data will be permanently deleted.
                            </p>
                        </div>
                    </div>

                    {/* Backup suggestion */}
                    <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
                        <ArrowDownTrayIcon className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
                        <div>
                            <p className="font-medium text-amber-800 dark:text-amber-300">
                                Download a backup first
                            </p>
                            <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                                <Link to="/data" className="underline hover:no-underline">
                                    Export your data
                                </Link>{' '}
                                before purging.
                            </p>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-8">
                        <Spinner size="lg" />
                    </div>
                ) : (
                    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-600 bg-white dark:bg-secondary">
                        {/* Entity summary - inline */}
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm mb-4">
                            <span className="font-medium text-gray-900 dark:text-white">
                                Data to be deleted:
                            </span>
                            <span className="text-gray-600 dark:text-gray-300">
                                {data?.collectionsCount ?? 0} collections
                            </span>
                            <span className="text-gray-600 dark:text-gray-300">
                                {data?.tagsCount ?? 0} tags
                            </span>
                            <span className="text-gray-600 dark:text-gray-300">
                                {data?.ledgersCount ?? 0} assets/debts
                                <span className="text-gray-500 dark:text-gray-400">
                                    {' '}({totalValues} values, {totalReturns} returns)
                                </span>
                            </span>
                        </div>

                        {totalEntities === 0 ? (
                            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                                No data to purge.
                            </p>
                        ) : (
                            <div className="space-y-4 border-t border-gray-200 dark:border-gray-600 pt-4">
                                <Checkbox
                                    checked={hasAcknowledgedBackup}
                                    onChange={(e) =>
                                        setHasAcknowledgedBackup(e.target.checked)
                                    }
                                    label="I have backed up my data or don't need a backup"
                                />

                                <div>
                                    <label className="mb-1 block text-sm text-gray-700 dark:text-gray-300">
                                        Type{' '}
                                        <span className="font-mono font-bold text-red-600 dark:text-red-400">
                                            {CONFIRMATION_PHRASE}
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        value={inputPhrase}
                                        onChange={(e) => setInputPhrase(e.target.value)}
                                        placeholder={CONFIRMATION_PHRASE}
                                        className="max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-red-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>

                                <Button
                                    variant="danger"
                                    onClick={handlePurge}
                                    isLoading={purgeAccount.isPending}
                                    disabled={
                                        !isPhraseCorrect ||
                                        !hasAcknowledgedBackup ||
                                        totalEntities === 0 ||
                                        isLoading
                                    }
                                >
                                    Purge All Data
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}
